import React from 'react';
import { useTranslation } from 'react-i18next';
import { View, StyleSheet, Pressable, type ViewStyle } from 'react-native';
import { useArchiveChannel, useMarkNeedMatchAs } from 'app/network/chat/channel';
import { ListingMatchStatus } from 'app/types/listing';
import { useNavigation } from 'app/hooks/navigation';
import { navigateToAllChannels } from 'app/screens/chat/navigator-helper';

import { Button } from 'app/design/primitives/button';
import { type Channel } from 'app/types/chat';
import { useOneListing } from 'app/network/listing';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import type { TFunction } from 'i18next';
import type { Listing } from 'app/types/listing';
const theme = createDSTheme();

const bottomSheetContainerStyle: ViewStyle = {
  backgroundColor: theme.colors['surface.default'],
  borderTopLeftRadius: 24,
  borderTopRightRadius: 24,
  paddingTop: 16,
  paddingHorizontal: 24,
  paddingBottom: 24,
};

interface ConciergeMessageProps {
  listingId: string;
  needMatchStatus?: ListingMatchStatus;
  interlocutorName?: string;
  isLender?: boolean;
  channelUrl: Channel['channel_url'];
}

const useNeedMatchLocalStatus = (needMatchStatus: ListingMatchStatus | undefined) => {
  const [override, setOverride] = React.useState<{
    source: ListingMatchStatus | undefined;
    value: ListingMatchStatus;
  } | null>(null);
  let currentOverride = override;
  if (override && override.source !== needMatchStatus) {
    currentOverride = null;
    setOverride(null);
  }
  return { needMatchLocalStatus: currentOverride?.value ?? needMatchStatus, setNeedMatchOverride: setOverride };
};

const ConciergeStatusCard = ({
  bullets,
  emoji,
  subtitle,
  title,
}: {
  bullets?: string[];
  emoji: string;
  subtitle?: string;
  title: string;
}) => (
  <View style={styles.card}>
    <View style={styles.emojiContainer}>
      <Text style={styles.emoji}>{emoji}</Text>
    </View>
    <Text style={styles.title}>{title}</Text>
    {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
    {bullets ? (
      <View style={styles.bulletsContainer}>
        {bullets.map((bullet) => (
          <Text key={bullet} style={styles.bullet}>
            {bullet}
          </Text>
        ))}
      </View>
    ) : null}
  </View>
);

const CanYouHelpCard = ({
  isWhyMessageOpen,
  onAccept,
  onCloseWhy,
  onNotificationPreferences,
  onOpenWhy,
  onReject,
  t,
}: {
  isWhyMessageOpen: boolean;
  onAccept: () => void;
  onCloseWhy: () => void;
  onNotificationPreferences: () => void;
  onOpenWhy: () => void;
  onReject: () => void;
  t: TFunction;
}) => (
  <View style={styles.card}>
    <View style={styles.emojiContainer}>
      <Text style={styles.emoji}>🧐</Text>
    </View>
    <Text style={styles.title}>{t('conciergeMessage.canYouHelp.title')}</Text>
    <Text style={styles.subtitle}>{t('conciergeMessage.canYouHelp.subtitle')}</Text>
    <View style={styles.buttonRow}>
      <Button onPress={onReject} variant="secondary" style={[styles.secondaryButton, styles.responseButton]}>
        <Text style={[styles.buttonText, styles.buttonOutlineText]}>{t('conciergeMessage.canYouHelp.buttonNo')}</Text>
      </Button>
      <Button onPress={onAccept} variant="primary" style={[styles.primaryButton, styles.responseButton]}>
        <Text style={[styles.buttonText, styles.buttonFilledText]}>{t('conciergeMessage.canYouHelp.buttonYes')}</Text>
      </Button>
    </View>
    <Pressable accessibilityRole="button" hitSlop={8} onPress={onOpenWhy}>
      <Text style={styles.whyMessageLink}>{t('conciergeMessage.canYouHelp.whyDidIReceiveThisMessage')}</Text>
    </Pressable>
    <BottomSheet
      onClose={onCloseWhy}
      open={isWhyMessageOpen}
      showIndicator={false}
      useBottomSafeAreaPadding={false}
      containerStyle={bottomSheetContainerStyle}
    >
      <Column paddingVertical="spacing.general.x4" gap="spacing.general.x4">
        <Row alignItems="center" justifyContent="space-between">
          <Column width={32} height={32} />
          <Text fontSize={20} lineHeight={24} fontWeight="700" textAlign="center" style={{ color: '#1F2635' }}>
            {t('conciergeMessage.canYouHelp.bottomSheet.title')}
          </Text>
          <Pressable accessibilityRole="button" hitSlop={10} onPress={onCloseWhy}>
            <IconX strokeWidth={ICON_STROKE_WIDTH} size={32} color={theme.colors['text.primary']} />
          </Pressable>
        </Row>
        <Text fontSize={16} lineHeight={20} style={{ color: '#1F2635' }}>
          {t('conciergeMessage.canYouHelp.bottomSheet.paragraph1')}
        </Text>
        <Text fontSize={16} lineHeight={20} style={{ color: '#1F2635' }}>
          {t('conciergeMessage.canYouHelp.bottomSheet.paragraph2')}
        </Text>
        <Text fontSize={16} lineHeight={20} style={{ color: '#1F2635' }}>
          {t('conciergeMessage.canYouHelp.bottomSheet.paragraph3.beforeBold')}
          <Text fontSize={16} lineHeight={20} fontWeight="700" style={{ color: '#1F2635' }}>
            {t('conciergeMessage.canYouHelp.bottomSheet.paragraph3.bold')}
          </Text>
          {t('conciergeMessage.canYouHelp.bottomSheet.paragraph3.afterBold')}
        </Text>
        <Pressable accessibilityRole="button" hitSlop={8} onPress={onNotificationPreferences}>
          <Text fontSize={16} lineHeight={20} fontWeight="700" letterSpacing={-0.32} style={{ color: '#2B8176' }}>
            {t('conciergeMessage.canYouHelp.bottomSheet.cta')}
          </Text>
        </Pressable>
      </Column>
    </BottomSheet>
  </View>
);

const LenderConciergeContent = ({
  interlocutorName,
  isWhyMessageOpen,
  listing,
  needMatchStatus,
  onAccept,
  onCloseWhy,
  onNotificationPreferences,
  onOpenWhy,
  onReject,
  t,
}: {
  interlocutorName: string | undefined;
  isWhyMessageOpen: boolean;
  listing: Listing | undefined;
  needMatchStatus: ListingMatchStatus | undefined;
  onAccept: () => void;
  onCloseWhy: () => void;
  onNotificationPreferences: () => void;
  onOpenWhy: () => void;
  onReject: () => void;
  t: TFunction;
}) => {
  if (needMatchStatus === ListingMatchStatus.ACCEPTED || needMatchStatus === ListingMatchStatus.MATCH) {
    return (
      <ConciergeStatusCard
        emoji="❤️"
        title={t('conciergeMessage.canHelp.title', { interlocutorName })}
        bullets={[t('conciergeMessage.canHelp.proposeDates'), t('conciergeMessage.canHelp.sendMessage')]}
      />
    );
  }
  if (needMatchStatus === ListingMatchStatus.REJECTED) {
    return (
      <ConciergeStatusCard
        emoji="👋"
        title={t('conciergeMessage.cantHelp.title', { interlocutorName })}
        subtitle={t('conciergeMessage.cantHelp.subtitle', { interlocutorName })}
      />
    );
  }
  if (needMatchStatus === ListingMatchStatus.RESOLVED && listing?.lender) {
    return (
      <ConciergeStatusCard
        emoji="🎉"
        title={t('conciergeMessage.resolved.title', { interlocutorName })}
        subtitle={t('conciergeMessage.resolved.subtitle', { interlocutorName })}
      />
    );
  }
  if (needMatchStatus === ListingMatchStatus.RESOLVED && !listing) return null;
  return (
    <CanYouHelpCard
      {...{
        isWhyMessageOpen,
        onAccept,
        onCloseWhy,
        onNotificationPreferences,
        onOpenWhy,
        onReject,
        t,
      }}
    />
  );
};

export const ConciergeMessage: React.FC<ConciergeMessageProps> = ({
  isLender,
  needMatchStatus,
  listingId,
  interlocutorName,
  channelUrl,
}: ConciergeMessageProps) => {
  const { t } = useTranslation('one-channel');
  const navigation = useNavigation();
  const { mutate: markNeedMatchAs } = useMarkNeedMatchAs();
  const { needMatchLocalStatus, setNeedMatchOverride } = useNeedMatchLocalStatus(needMatchStatus);
  const [isWhyMessageOpen, setIsWhyMessageOpen] = React.useState(false);
  const { data: listing } = useOneListing(listingId, 'one-channel', { enabled: !!listingId });

  const { mutate: archiveChannel } = useArchiveChannel({ invalidateRecents: true });
  const onPressAccept = () => {
    const previousStatus = needMatchLocalStatus;
    setNeedMatchOverride({ source: needMatchStatus, value: ListingMatchStatus.ACCEPTED });
    markNeedMatchAs(
      { channelUrl, listingId, status: ListingMatchStatus.ACCEPTED },
      {
        onError() {
          setNeedMatchOverride(previousStatus ? { source: needMatchStatus, value: previousStatus } : null);
        },
      },
    );
  };
  const onPressReject = () => {
    const previousStatus = needMatchLocalStatus;
    setNeedMatchOverride({ source: needMatchStatus, value: ListingMatchStatus.REJECTED });
    markNeedMatchAs(
      { channelUrl, listingId, status: ListingMatchStatus.REJECTED },
      {
        onSuccess: () => {
          archiveChannel(channelUrl, {
            onSuccess: () => {
              navigateToAllChannels(navigation, { archivedConvoChannelUrl: channelUrl });
            },
          });
        },
        onError: () => {
          setNeedMatchOverride(previousStatus ? { source: needMatchStatus, value: previousStatus } : null);
        },
      },
    );
  };
  const onPressNotificationPreferences = () => {
    setIsWhyMessageOpen(false);
    navigation.navigate('notifications-setting', undefined, { pop: true });
  };
  if (!needMatchStatus || needMatchLocalStatus === ListingMatchStatus.ABORTED) return null;
  if (!isLender) {
    if (needMatchLocalStatus !== ListingMatchStatus.ACCEPTED) return null;
    return (
      <ConciergeStatusCard
        emoji="🤝"
        title={t('conciergeMessage.matchAccepted.title', { interlocutorName })}
        bullets={[t('conciergeMessage.matchAccepted.proposeDates'), t('conciergeMessage.matchAccepted.sendMessage')]}
      />
    );
  }

  return (
    <LenderConciergeContent
      {...{ interlocutorName, isWhyMessageOpen, listing, needMatchStatus: needMatchLocalStatus, t }}
      onAccept={onPressAccept}
      onCloseWhy={() => setIsWhyMessageOpen(false)}
      onNotificationPreferences={onPressNotificationPreferences}
      onOpenWhy={() => setIsWhyMessageOpen(true)}
      onReject={onPressReject}
    />
  );
};

const styles = StyleSheet.create({
  card: {
    width: '100%',
    alignItems: 'center',
  },
  emojiContainer: {
    width: 75,
    height: 50,
    borderRadius: 28,
    backgroundColor: '#F8CAAA',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  emoji: {
    fontSize: 24,
  },
  title: {
    fontWeight: '700',
    fontSize: 16,
    lineHeight: 22,
    color: '#1F2635',
    textAlign: 'center',
    marginBottom: 4,
  },
  subtitle: {
    color: '#595B5E',
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 16,
    width: 214,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
    gap: 8,
    width: '100%',
    maxWidth: 247,
  },
  buttonText: {
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 16,
    letterSpacing: -0.14,
  },
  responseButton: {
    flex: 1,
    minWidth: 0,
  },
  primaryButton: {
    height: 48,
    borderRadius: 24,
    backgroundColor: '#1F2635',
  },
  secondaryButton: {
    height: 48,
    borderRadius: 24,
    borderColor: '#1F2635',
    borderWidth: 1,
    backgroundColor: '#FFFFFF',
  },
  buttonOutlineText: {
    color: '#1F2635',
  },
  buttonFilledText: {
    color: '#fff',
  },
  bulletsContainer: {
    alignSelf: 'stretch',
    marginTop: 14,
    paddingLeft: theme.spacing['spacing.general.x1'],
  },
  bullet: {
    fontSize: 14,
    lineHeight: 20,
    color: theme.colors['text.primary'],
    marginBottom: 6,
  },
  footer: {
    color: theme.colors['text.tertiary'],
    fontSize: 10,
    alignSelf: 'stretch',
    paddingTop: theme.spacing['spacing.general.x4'],
  },
  whyMessageLink: {
    color: '#1F2635',
    fontSize: 14,
    lineHeight: 17,
    textDecorationLine: 'underline',
    textDecorationColor: '#1F2635',
  },
});
